/* ============================================================
   Модальная форма заявки.
   - открывается по клику на карточку услуги (с предвыбором услуги)
     и по кнопке «Получить консультацию» в hero;
   - переключатель способа связи меняет плейсхолдер поля контакта;
   - сабмит вызывает submitOrder() и показывает экран успеха.
   ============================================================ */
import { onLangChange, getLang, dict } from '../i18n';
import { byId, qs, qsa } from '../dom';
import { submitOrder } from '../api';
import type { ContactMethod, Dictionary, OrderPayload } from '../types';

export interface FormControls {
  /** Открыть форму. serviceIndex — индекс услуги или null. */
  openForm: (serviceIndex: string | null) => void;
  closeForm: () => void;
}

export function createForm(): FormControls {
  const modal = byId('formModal');
  const form = byId<HTMLFormElement>('orderForm');
  const body = byId('formBody');
  const success = byId('formSuccess');
  const company = byId<HTMLInputElement>('fCompany');
  const email = byId<HTMLInputElement>('fEmail');
  const contact = byId<HTMLInputElement>('fContact');
  const comment = byId<HTMLTextAreaElement>('fComment');
  const serviceSel = byId<HTMLSelectElement>('fService');

  let method: ContactMethod = 'tg';

  function setMethodLabel(m: ContactMethod, text: string): void {
    const ml = qs<HTMLElement>(`[data-m="${m}"] .ml`);
    if (ml) ml.textContent = text;
  }

  function updateContactPlaceholder(): void {
    const d = dict();
    const map: Record<ContactMethod, string> = {
      tg: d.f_contact_tg,
      phone: d.f_contact_phone,
      wa: d.f_contact_wa,
    };
    contact.placeholder = map[method];
  }

  function renderForm(d: Dictionary): void {
    company.placeholder = d.f_company;
    email.placeholder = d.f_email;
    comment.placeholder = d.f_comments;

    setMethodLabel('tg', d.f_m_tg);
    setMethodLabel('phone', d.f_m_phone);
    setMethodLabel('wa', d.f_m_wa);

    const prev = serviceSel.value;
    serviceSel.innerHTML = d.services
      .map((s, i) => `<option value="${i}">${s[0]}</option>`)
      .join('');
    if (prev !== '') serviceSel.value = prev;

    updateContactPlaceholder();
  }

  onLangChange(renderForm);

  function openForm(serviceIndex: string | null): void {
    body.hidden = false;
    success.hidden = true;

    if (serviceIndex !== null && serviceIndex !== '' && !Number.isNaN(Number(serviceIndex))) {
      serviceSel.value = String(Number(serviceIndex));
    }

    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => company.focus(), 140);
  }

  function closeForm(): void {
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function resetMethod(): void {
    method = 'tg';
    qsa<HTMLElement>('.method-btn').forEach((x) => x.classList.toggle('on', x.dataset.m === 'tg'));
    updateContactPlaceholder();
  }

  // Переключатель способа связи
  byId('methodToggle').addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.method-btn');
    const m = btn?.dataset.m;
    if (m !== 'tg' && m !== 'phone' && m !== 'wa') return;
    method = m;
    qsa<HTMLElement>('.method-btn').forEach((x) => x.classList.toggle('on', x === btn));
    updateContactPlaceholder();
  });

  byId('formClose').addEventListener('click', closeForm);
  byId('succClose').addEventListener('click', closeForm);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeForm();
  });

  // Сабмит
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const idx = Number(serviceSel.value);
    const payload: OrderPayload = {
      company: company.value.trim(),
      email: email.value.trim(),
      method,
      contact: contact.value.trim(),
      serviceIndex: idx,
      serviceName: dict().services[idx]?.[0] ?? '',
      comment: comment.value.trim(),
      lang: getLang(),
    };

    void submitOrder(payload)
      .then(() => {
        body.hidden = true;
        success.hidden = false;
        form.reset();
        resetMethod();
      })
      .catch((err: unknown) => {
        console.error('[order] submit failed', err);
        window.alert('Не удалось отправить заявку. Попробуйте позже.');
      });
  });

  return { openForm, closeForm };
}
